import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { login, logout, check, switchAuth, verifyLoginFind } from '../api/auth.api';
import { IAuth, IGetAuth, IAuthReq, InitialAuth, IGetAuthFind } from '../models/auth.model';
import { AppThunk } from './index';

interface IAuthState {
  result?: string;
  auth: IAuth;
  isLogin: boolean;
  isLoading: boolean;
  isDarkMode: boolean;
  currentCampus: string;
  currentSemesterCode: number|undefined;
  error?: AxiosError | string;
  isLoginCertificationModal: boolean;
}

const initialState: IAuthState = {
  result: undefined,
  auth: InitialAuth,
  isLoading: false,
  isLogin: false,
  isDarkMode: false,
  currentCampus: '',
  currentSemesterCode: undefined,
  isLoginCertificationModal: false
};

const authSlicer = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    requestStart: (state: IAuthState) => {
      state.isLoading = true;
      state.error = undefined;
      state.isLogin = false;
    },
    requestFailure: (state: IAuthState, action: PayloadAction<AxiosError|string>) => {
      state.isLoading = false;
      state.error = action.payload;
      state.isLogin = false;
    },
    loginSuccess: (state: IAuthState, action: PayloadAction<IGetAuth>) => {
      state.result = action.payload.result;
      state.auth = action.payload.data;
      state.isLogin = true;
      state.isLoading = false;
      state.error = undefined;
    },
    loginFindSuccess: (state: IAuthState, action: PayloadAction<IGetAuthFind>) => {
      state.result = action.payload.result;
      state.isLoading = false;
      state.error = undefined;
    },
    logoutSuccess: (state: IAuthState) => {
      state.result = undefined;
      state.auth = InitialAuth;
      state.isLoading = false;
      state.error = undefined;
      state.isLogin = false;
    },
    checkSuccess: (state: IAuthState, action: PayloadAction<IGetAuth>) => {
      state.result = action.payload.result;
      state.auth = action.payload.data;
      state.isLogin =  true;          
      state.isLoading = false;
      state.error = undefined;
    },
    setCurrentCampus: (state: IAuthState, action: PayloadAction<string>) => {
      state.currentCampus = action.payload;
    },
    setCurrentSemesterCode: (state: IAuthState, action: PayloadAction<number|undefined>) => {
      state.currentSemesterCode = action.payload;
    },
    setIsDarkMode: (state: IAuthState, action: PayloadAction<boolean>) => {
      state.isDarkMode = action.payload;
    },
    setLoginCertificationModal: (state: IAuthState, action: PayloadAction<boolean>) => {
      state.isLoginCertificationModal = action.payload;
    }
  }
});

export const {
  requestStart,
  requestFailure,
  loginSuccess,
  logoutSuccess,
  checkSuccess,
  setCurrentCampus,
  setCurrentSemesterCode,
  setIsDarkMode,
  setLoginCertificationModal,
  loginFindSuccess
} = authSlicer.actions;

export default authSlicer.reducer;

export const doFind = (data: IAuthReq): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await verifyLoginFind(data);
    dispatch(loginFindSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doLogin = (data: IAuthReq): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await login(data);
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doLogout = (): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await logout();
    dispatch(logoutSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doCheck = (): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await check();
    dispatch(checkSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doRemoveAuth = (): AppThunk => async (dispatch) => {
  dispatch(logoutSuccess());
};

export const doSwitchAuth = (id: string): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await switchAuth(id);
    dispatch(loginSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doSetCurrentCampus = (e: any): any => async (dispatch: any) => {
  dispatch(setCurrentCampus(e));
};

export const doSetCurrentSemesterCode = (e: any): any => async (dispatch: any) => {
  dispatch(setCurrentSemesterCode(e));
};
